// MegaMenuV3 — trigger + panel with 3 columns (Prodotto, Soluzioni, Risorse).
//
// Trigger opens on hover (desktop) or click (touch). Panel is a single
// horizontal card anchored below the header. ESC + outside click close it.
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { MENU_GROUPS } from "./menuData";
import MenuColumnV3 from "./MenuColumnV3";

export default function MegaMenuV3() {
    const { t } = useTranslation("header_v3");
    const [openKey, setOpenKey] = useState(null);
    const wrapperRef = useRef(null);
    const closeTimer = useRef(null);

    // Close on outside click / ESC
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") setOpenKey(null); };
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpenKey(null);
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onClick);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onClick);
            if (closeTimer.current) clearTimeout(closeTimer.current);
        };
    }, []);

    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpenKey(null), 180);
    };
    const cancelClose = () => {
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    };

    return (
        <nav className="hidden lg:block" ref={wrapperRef} aria-label="Primary" data-testid="header-v3-megamenu-nav">
            <div className="flex items-center gap-1">
                {MENU_GROUPS.map((group) => {
                    const isOpen = openKey === group.key;
                    return (
                        <div
                            key={group.key}
                            className="relative"
                            onMouseEnter={() => { cancelClose(); setOpenKey(group.key); }}
                            onMouseLeave={scheduleClose}
                        >
                            <button
                                type="button"
                                onClick={() => setOpenKey(isOpen ? null : group.key)}
                                aria-expanded={isOpen}
                                aria-haspopup="true"
                                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-white/90 hover:text-white hover:bg-white/[0.04] transition-colors"
                                data-testid={`header-v3-megamenu-${group.key}`}
                            >
                                {t(group.i18n)}
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} style={{ color: "#FFFFFF" }} />
                            </button>
                        </div>
                    );
                })}
                {/* Direct links (Prezzi) — no dropdown */}
                <a
                    href="/v3#pricing"
                    className="inline-flex items-center px-3 py-2 rounded-lg text-sm text-white/90 hover:text-white hover:bg-white/[0.04] transition-colors no-underline"
                    data-testid="header-v3-link-prezzi"
                >
                    {t("menu.prezzi")}
                </a>
            </div>

            {/* Shared full-width panel */}
            <AnimatePresence>
                {openKey && (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 2 }}
                        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute left-6 right-6 top-full mt-2 rounded-2xl border border-white/5 shadow-[0_20px_60px_rgba(0,0,0,0.5)] p-6 z-40"
                        style={{ backgroundColor: "#111111" }}
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                        data-testid="header-v3-megamenu-panel"
                    >
                        <div className="grid grid-cols-3 gap-8 max-w-5xl mx-auto">
                            {MENU_GROUPS.map((group) => (
                                <div key={group.key} className={openKey === group.key ? "" : "opacity-70"}>
                                    <MenuColumnV3 group={group} testIdPrefix="header-v3-megamenu" />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
}
